const WRAPPER_SELECTOR = ".table-wrapper--scroll-y";
const SCROLLBAR_WIDTH = 6;

/**
 * @param {number[]} widths
 * @param {number} targetTotal
 */
function normalizeWidths(widths, targetTotal) {
  if (widths.length === 0 || targetTotal <= 0) return widths;

  const sum = widths.reduce((total, width) => total + width, 0);
  if (sum <= 0) return widths;

  const scaled = widths.map((width) => Math.round((width / sum) * targetTotal));
  const diff = targetTotal - scaled.reduce((total, width) => total + width, 0);
  scaled[scaled.length - 1] += diff;
  return scaled;
}

/**
 * @param {HTMLTableRowElement} row
 */
function measureRowWidths(row) {
  return [...row.children].map((cell) => {
    if (!(cell instanceof HTMLElement)) return 0;
    return cell.offsetWidth;
  });
}

/**
 * @param {HTMLTableSectionElement} tbody
 * @returns {HTMLTableRowElement | null}
 */
function findMeasureRow(tbody) {
  const rows = tbody.querySelectorAll("tr:not(.empty-row)");
  for (const row of rows) {
    if (
      row instanceof HTMLTableRowElement &&
      row.children.length > 1 &&
      row.offsetParent !== null
    ) {
      return row;
    }
  }

  const fallback = tbody.querySelector("tr:not(.empty-row)");
  return fallback instanceof HTMLTableRowElement ? fallback : null;
}

/**
 * @param {HTMLTableElement} table
 * @returns {number[] | null}
 */
function measureColumnRatios(table) {
  const tbody = table.querySelector("tbody");
  const row = tbody ? findMeasureRow(tbody) : null;
  if (!row) return null;

  const widths = measureRowWidths(row);
  const sum = widths.reduce((total, width) => total + width, 0);
  if (sum <= 0) return null;

  return widths.map((width) => width / sum);
}

/**
 * @param {HTMLTableElement} table
 * @param {number[]} widths
 */
function applyColgroup(table, widths) {
  let colgroup = table.querySelector("colgroup");
  if (!colgroup) {
    colgroup = document.createElement("colgroup");
    const caption = table.querySelector("caption");
    if (caption) {
      caption.after(colgroup);
    } else {
      table.insertBefore(colgroup, table.firstChild);
    }
  }

  colgroup.replaceChildren(
    ...widths.map((width) => {
      const col = document.createElement("col");
      col.style.width = `${width}px`;
      return col;
    }),
  );
}

/**
 * @param {HTMLElement} wrapper
 */
function getTableIdPrefix(wrapper) {
  if (wrapper.id) return wrapper.id;

  const existing = wrapper.dataset.tableA11yId;
  if (existing) return existing;

  const generated = `table-scroll-${Math.random().toString(36).slice(2, 9)}`;
  wrapper.dataset.tableA11yId = generated;
  return generated;
}

/**
 * body 테이블용 scope / id / headers 를 보강합니다.
 * @param {HTMLTableElement} table
 * @param {HTMLElement} wrapper
 */
function enhanceTableAccessibility(table, wrapper) {
  const thead = table.querySelector("thead");
  const tbody = table.querySelector("tbody");
  if (!thead || !tbody) return;

  const headerRows = [...thead.querySelectorAll("tr")];
  const isSimpleHeader = headerRows.length === 1;
  const prefix = getTableIdPrefix(wrapper);

  /** @type {string[]} */
  const columnIds = [];

  thead.querySelectorAll("th").forEach((th, index) => {
    if (!(th instanceof HTMLTableCellElement)) return;
    if (!th.getAttribute("scope")) {
      th.setAttribute("scope", "col");
    }
    if (!th.id) {
      th.id = `${prefix}-col-${index + 1}`;
    }
  });

  if (isSimpleHeader) {
    [...headerRows[0].children].forEach((cell) => {
      if (!(cell instanceof HTMLTableCellElement)) return;
      if (cell.tagName !== "TH" || !cell.id) return;
      columnIds.push(cell.id);
    });
  } else {
    const firstHeaderRow = headerRows[0];
    const lastHeaderRow = headerRows[headerRows.length - 1];
    const modelTh = [...(firstHeaderRow?.children || [])].find(
      (cell) =>
        cell instanceof HTMLTableCellElement &&
        cell.tagName === "TH" &&
        cell.rowSpan > 1,
    );

    if (modelTh instanceof HTMLTableCellElement && modelTh.id) {
      columnIds.push(modelTh.id);
    }

    [...(lastHeaderRow?.children || [])].forEach((cell) => {
      if (!(cell instanceof HTMLTableCellElement)) return;
      if (cell.tagName !== "TH" || cell.colSpan > 1 || !cell.id) return;
      columnIds.push(cell.id);
    });
  }

  [...tbody.querySelectorAll("tr")].forEach((row, rowIndex) => {
    if (!(row instanceof HTMLTableRowElement)) return;
    if (row.classList.contains("empty-row")) return;

    const cells = [...row.children].filter(
      (cell) => cell instanceof HTMLTableCellElement,
    );
    if (cells.length === 0) return;

    let firstCell = cells[0];

    if (firstCell.tagName === "TD") {
      const rowTh = document.createElement("th");
      rowTh.scope = "row";
      rowTh.className = firstCell.className;
      rowTh.innerHTML = firstCell.innerHTML;
      [...firstCell.attributes].forEach((attr) => {
        if (attr.name === "class") return;
        rowTh.setAttribute(attr.name, attr.value);
      });
      firstCell.replaceWith(rowTh);
      cells[0] = rowTh;
      firstCell = rowTh;
    }

    let rowId = firstCell.id;

    if (firstCell.tagName === "TH") {
      if (!firstCell.getAttribute("scope")) {
        firstCell.setAttribute("scope", "row");
      }
      if (!rowId) {
        rowId = `${prefix}-row-${rowIndex + 1}`;
        firstCell.id = rowId;
      }
      firstCell.removeAttribute("headers");
    }

    if (columnIds.length === 0) return;

    cells.forEach((cell, cellIndex) => {
      if (cell.tagName === "TH" && cellIndex === 0) return;

      const colId = columnIds[cellIndex];
      if (!colId) return;

      const headerRefs = [colId];
      if (rowId) headerRefs.push(rowId);
      cell.setAttribute("headers", headerRefs.join(" "));
    });
  });
}

/**
 * 시각용 헤더 / 데이터 테이블로 분리합니다.
 * - head: 화면용, 스크린리더 제외 (aria-hidden)
 * - body: caption + 시각적으로 숨긴 thead(SR용, scope/id) + tbody(headers)
 * @param {HTMLElement} wrapper
 */
function splitScrollTable(wrapper) {
  if (wrapper.querySelector(".table-scroll__body")) return;

  const table = wrapper.querySelector(".table");
  const thead = table?.querySelector("thead");
  const tbody = table?.querySelector("tbody");
  if (!(table instanceof HTMLTableElement) || !thead || !tbody) return;

  // 분리 전에 scope / id / headers 보강 → body 쪽으로 이어짐
  enhanceTableAccessibility(table, wrapper);

  const caption = table.querySelector("caption");
  const sourceColgroup = table.querySelector("colgroup");

  // 시각용 헤더 테이블 — 스크린리더에서 읽지 않음
  const headTable = document.createElement("table");
  headTable.className = table.className;
  if (sourceColgroup) {
    headTable.appendChild(sourceColgroup.cloneNode(true));
  }
  headTable.appendChild(thead);

  // 데이터 테이블 — caption + SR용 thead + tbody
  const bodyTable = document.createElement("table");
  bodyTable.className = table.className;

  if (caption) {
    bodyTable.appendChild(caption);
  }

  if (sourceColgroup) {
    bodyTable.appendChild(sourceColgroup);
  }

  // thead 복제: 화면에는 숨기고, id/scope는 body 쪽에 유지 (headers 참조용)
  const a11yThead = /** @type {HTMLTableSectionElement} */ (
    thead.cloneNode(true)
  );
  a11yThead.classList.add("table-scroll__sr-thead");

  const visualThs = thead.querySelectorAll("th");
  const a11yThs = a11yThead.querySelectorAll("th");
  visualThs.forEach((th, index) => {
    const a11yTh = a11yThs[index];
    if (!(th instanceof HTMLElement) || !(a11yTh instanceof HTMLElement)) return;

    if (th.id) {
      a11yTh.id = th.id;
      th.removeAttribute("id");
    }
  });

  bodyTable.appendChild(a11yThead);
  bodyTable.appendChild(tbody);

  const headWrap = document.createElement("div");
  headWrap.className = "table-scroll__head";
  headWrap.setAttribute("aria-hidden", "true");
  headWrap.appendChild(headTable);

  const bodyWrap = document.createElement("div");
  bodyWrap.className = "table-scroll__body";
  bodyWrap.appendChild(bodyTable);

  const fragment = document.createDocumentFragment();
  fragment.append(headWrap, bodyWrap);
  table.replaceWith(fragment);
}

/**
 * CSS 길이 값(rem/px 등)을 px로 변환합니다.
 * @param {string} value
 * @param {HTMLElement} referenceEl
 */
function resolveCssLengthPx(value, referenceEl) {
  const trimmed = value.trim();
  if (!trimmed) return NaN;

  if (trimmed.endsWith("px")) {
    const px = Number.parseFloat(trimmed);
    return Number.isFinite(px) ? px : NaN;
  }

  const probe = document.createElement("div");
  probe.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;height:0;width:${trimmed}`;
  referenceEl.appendChild(probe);
  const px = probe.offsetWidth;
  probe.remove();
  return px > 0 ? px : NaN;
}

/**
 * @param {HTMLElement} wrapper
 */
function getMinTableWidth(wrapper) {
  const raw = getComputedStyle(wrapper).getPropertyValue("--table-min-width");
  const fromVar = resolveCssLengthPx(raw, wrapper);
  if (Number.isFinite(fromVar) && fromVar > 0) return fromVar;
  return 900;
}

/**
 * @param {HTMLElement} wrapper
 * @param {number[] | null} columnRatios
 */
function syncTableScrollWrapper(wrapper, columnRatios) {
  const headWrap = wrapper.querySelector(".table-scroll__head");
  const headTable = wrapper.querySelector(".table-scroll__head .table");
  const bodyWrap = wrapper.querySelector(".table-scroll__body");
  const bodyTable = wrapper.querySelector(".table-scroll__body .table");
  const tbody = bodyTable?.querySelector("tbody");
  const bodyRow = tbody ? findMeasureRow(tbody) : null;

  if (
    !(headWrap instanceof HTMLElement) ||
    !(headTable instanceof HTMLTableElement) ||
    !(bodyWrap instanceof HTMLElement) ||
    !(bodyTable instanceof HTMLTableElement) ||
    !bodyRow
  ) {
    return;
  }

  wrapper.style.setProperty("--table-scrollbar-width", `${SCROLLBAR_WIDTH}px`);

  // 뷰포트가 min-width보다 작으면 테이블은 min 유지 → wrapper에 가로 스크롤 생성
  const cssMinWidth = getMinTableWidth(wrapper);
  const tableWidth = Math.max(wrapper.clientWidth, cssMinWidth);
  if (tableWidth <= 0) return;

  const tableWidthPx = `${tableWidth}px`;

  // wrap 너비 = 테이블 너비 (같아야 우측 잔여 스크롤 없이, 좁을 땐 가로 스크롤 가능)
  headWrap.style.width = tableWidthPx;
  bodyWrap.style.width = tableWidthPx;
  headTable.style.width = tableWidthPx;
  bodyTable.style.width = tableWidthPx;

  let widths;
  if (columnRatios?.length === bodyRow.children.length) {
    widths = normalizeWidths(
      columnRatios.map((ratio) => ratio * tableWidth),
      tableWidth,
    );
  } else {
    widths = normalizeWidths(measureRowWidths(bodyRow), tableWidth);
  }

  if (widths.length === 0) return;

  const widthKey = `${tableWidth}:${widths.join(",")}`;
  if (wrapper.dataset.tableScrollWidths === widthKey) return;
  wrapper.dataset.tableScrollWidths = widthKey;

  applyColgroup(headTable, widths);
  applyColgroup(bodyTable, widths);
}

/**
 * @param {HTMLElement} wrapper
 */
function bindTableScrollWrapper(wrapper) {
  if (wrapper.dataset.tableScrollReady === "true") return;

  const sourceTable = wrapper.querySelector(".table");

  const columnRatios =
    sourceTable instanceof HTMLTableElement
      ? measureColumnRatios(sourceTable)
      : null;

  splitScrollTable(wrapper);

  const bodyWrap = wrapper.querySelector(".table-scroll__body");
  if (!(bodyWrap instanceof HTMLElement)) return;

  wrapper.dataset.tableScrollReady = "true";

  let frame = 0;
  const sync = () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      syncTableScrollWrapper(wrapper, columnRatios);
    });
  };

  sync();
  window.requestAnimationFrame(sync);

  const resizeObserver = new ResizeObserver(sync);
  resizeObserver.observe(bodyWrap);
  resizeObserver.observe(wrapper);

  if (document.fonts?.ready) {
    document.fonts.ready.then(sync).catch(() => {});
  }

  if ("MutationObserver" in window) {
    const mutationObserver = new MutationObserver(sync);
    mutationObserver.observe(bodyWrap, { childList: true, subtree: true });
  }
}

/**
 * @param {ParentNode} [root]
 */
function initTableScroll(root = document) {
  root.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
    if (wrapper instanceof HTMLElement) bindTableScrollWrapper(wrapper);
  });
}

function bootTableScroll() {
  initTableScroll();

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target instanceof HTMLElement) initTableScroll(event.target);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootTableScroll);
} else {
  bootTableScroll();
}
